import { Redirect } from 'expo-router'
import { useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { FlatList, InteractionManager, Platform, View, type ListRenderItem, type TextInput } from 'react-native'

import { Spacer } from '@/src/elements/spacer/Spacer'
import { Typography } from '@/src/elements/text/Typography'
import { TEST_ID } from '@/src/general/constants/testId'
import { useDimensions } from '@/src/general/hooks/useDimensions'
import { TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import { useRoutes } from '@/src/general/utils/routes'

import { COMMUNITY_FILTER, EVENT_ORIGIN } from '../../../models/community.constants'
import { useCommunitiesQuery } from '../../../services/useCommunitiesQuery'
import { CommunityCard } from '../../components/CommunityCard'
import { CommunityCategories } from '../../components/CommunityCategories'
import { CommunitySearchPlaceholder } from '../../components/CommunitySearchPlaceholder'
import { SearchBar } from '../../components/SearchBar'
import { useLimitItem } from '../../hooks/useLimits'
import { AnimatedSearchBar } from './AnimatedSearchBar'

const ListEmptyComponent = () => <CommunityCategories />
const ItemSeparatorComponent = () => <Spacer y={4} />

/**
 * The CommunitiesMobileSearch screen.
 *
 * This screen is responsible for rendering the search bar and the list of
 * communities that match the search query.
 *
 * When the user types in the search bar, the query is debounced and the
 * useCommunitiesQuery hook is called with the debounced query as the
 * filterParam.
 *
 * The list of communities is rendered as a FlatList with a header and a
 * footer. The header displays the search bar and the categories, and the
 * footer displays a placeholder for the loading state.
 *
 * When the user reaches the end of the list, the hook fetchNextPage is
 * called to fetch the next page of data.
 */

export const CommunitiesMobileSearch = () => {
  const ref = useRef<TextInput>(null)
  const [searchValue, setSearchValue] = useState('')
  const { xm, xs, sm } = useDimensions()

  const flatStaticProps = {
    numColumns: xm ? 2 : undefined,
    columnWrapperClassName: xm ? 'gap-6' : undefined,
  }

  const { routes } = useRoutes()

  const { limit } = useLimitItem()

  const handleChangeText = (searchStr: string) => {
    setSearchValue(searchStr)
  }

  const queryEnabled = searchValue.length > 2
  const { data, fetchNextPage, isFetchingNextPage, isPending, isFetching, hasNextPage, fetchStatus } =
    useCommunitiesQuery({
      filter: COMMUNITY_FILTER.KEYWORD,
      filterParam: searchValue,
      fields: ['lastPublication', 'hasJoined', 'sharesCount'],
      limit,
      meta: ['globalCount', 'hasMore'],
      enabled: queryEnabled,
    })

  // Focus input to trigger keyboard show event
  useEffect(() => {
    setTimeout(() => {
      InteractionManager.runAfterInteractions(() => {
        ref.current?.focus()
      })
    }, 200)
  }, [])

  const ListFooterComponent = () => {
    if ((isFetching || isPending) && queryEnabled) {
      return Array.from({ length: 4 }, (_, index) => (
        <View key={index} className="flex w-full xm:flex-row xm:flex-wrap xm:gap-4 ">
          <CommunitySearchPlaceholder />
          {xm && <CommunitySearchPlaceholder key={index + 'bis'} />}
        </View>
      ))
    }
    return null
  }

  const renderItem: ListRenderItem<NonNullable<typeof data>[number]> = ({ item: community }) => (
    <View className="native:h-[335px] bg-white">
      <CommunityCard
        fullWidth={sm || xs || xm}
        community={community}
        origin={EVENT_ORIGIN.COMMUNITIES_SEARCH}
        source={TRACKING_SOURCE.COMMUNITIES_SEARCH}
      />
    </View>
  )

  if (!xm && !xs && !sm) {
    return <Redirect href={routes.communities()} />
  }

  return (
    <View testID={TEST_ID.COMMUNITIES.MOBILE_SEARCH_SCREEN} className="flex-1">
      <View className="pl-8 pr-6 native:pl-6">
        <AnimatedSearchBar>
          <SearchBar
            onChangeText={handleChangeText}
            ref={ref}
            autofocus={true}
            searchValue={searchValue}
            testID={TEST_ID.COMMUNITIES.SEARCH_INPUT_MOBILE}
          />
        </AnimatedSearchBar>
      </View>
      <FlatList
        key={flatStaticProps.numColumns}
        keyExtractor={(item) => `${item.id}`}
        {...flatStaticProps}
        className="mt-[-50px] px-6 flex-1"
        data={queryEnabled ? data : []}
        ListHeaderComponent={() => (
          <>
            <Spacer y={Platform.select({ native: 10, web: 20 })} />
            <Typography black className="mb-4 px-4" size="lg" colors="primary" contrast="high">
              {(data || (isPending && fetchStatus !== 'idle')) && <FormattedMessage id="communities.search" />}
              {fetchStatus === 'idle' && !data && <FormattedMessage id="communities.categories" />}
            </Typography>
          </>
        )}
        ListEmptyComponent={isPending && fetchStatus !== 'idle' ? null : ListEmptyComponent}
        onEndReached={() => {
          if (!isFetchingNextPage && data && hasNextPage) {
            fetchNextPage()
          }
        }}
        ListFooterComponent={ListFooterComponent}
        ItemSeparatorComponent={ItemSeparatorComponent}
        renderItem={renderItem}
      />
    </View>
  )
}
